<template>
  <div class="import-id-container">
    <div class="import-id-wrapper">
      <!-- 简化的标题区 -->
      <div class="page-header">
        <h2>导入私聊数字ID</h2>
        <div class="step-dots">
          <span 
            v-for="step in 3" 
            :key="step"
            :class="['dot', { active: currentStep >= step - 1 }]"
          ></span>
        </div>
      </div>

      <!-- 紧凑的内容区 -->
      <div class="main-content">
        <!-- 上传区 -->
        <transition name="fade">
          <div v-if="currentStep === 0" class="upload-zone">
            <a-upload-dragger
              v-model:fileList="fileList"
              name="file"
              :multiple="false"
              :before-upload="beforeUpload"
              @change="handleChange"
              accept=".txt"
              class="uploader"
            >
              <div class="upload-inner">
                <inbox-outlined class="icon" />
                <div class="text-group">
                  <p class="main-text">点击或拖拽文件上传</p>
                  <p class="sub-text">支持 .txt 格式</p>
                </div>
              </div>
            </a-upload-dragger>

            <div class="upload-actions" v-if="fileList.length > 0">
              <a-button type="primary" :loading="loading" @click="handleImport">导入</a-button>
              <a-button @click="clearFile">取消</a-button>
            </div>
          </div>
        </transition>

        <!-- 结果区 -->
        <transition name="fade">
          <div v-if="currentStep > 0" class="result-zone">
            <div class="alert-container" v-if="importStatus">
              <a-alert
                :message="importStatus.message"
                :type="importStatus.type"
                show-icon
                class="custom-alert"
                :class="importStatus.type"
                banner
              />
            </div>

            <div class="stats-container" v-if="parseResults">
              <div class="stat-box">
                <span class="stat-number">{{ parseResults.totalRows }}</span>
                <span class="stat-label">总行数</span>
              </div>
              <div class="stat-box">
                <span class="stat-number">{{ parseResults.columnCount }}</span>
                <span class="stat-label">字段数</span>
              </div>
              <div class="stat-box">
                <span class="stat-number success">成功</span>
                <span class="stat-label">状态</span>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- 简化的底部工具栏 -->
      <div class="footer">
        <a-button @click="resetImport" v-if="currentStep > 0">重新导入</a-button>
        <a-popconfirm
          title="确定要删除所有私聊数据吗?"
          @confirm="handleDeleteAll"
          okText="确定"
          cancelText="取消"
        >
          <a-button danger :loading="deleteLoading">删除全部</a-button>
        </a-popconfirm>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineEmits } from "vue";
import { InboxOutlined, QuestionCircleOutlined, Statistic } from "@ant-design/icons-vue";
import type { UploadChangeParam } from "ant-design-vue";
import { TelegramClient } from "@/websocket/Client";

// 定义事件
const emit = defineEmits<{
  (e: "import-id-success", data: any[]): void;
  (e: "import-id-error", error: string): void;
}>();

const fileList = ref<any[]>([]);
const loading = ref(false);
const importStatus = ref<{
  message: string;
  type: "success" | "error" | "info";
} | null>(null);

// 添加解析结果的响应式引用
const parseResults = ref<{
  totalRows: number;
  columnCount: number;
  columns: string[];
  previewColumns: any[];
  previewData: any[];
} | null>(null);

// 添加删除加载状态
const deleteLoading = ref(false);

// 添加步骤控制
const currentStep = ref(0);

// 添加重置方法
const resetImport = () => {
  currentStep.value = 0;
  clearFile();
  parseResults.value = null;
  importStatus.value = null;
};

// 上传前校验
const beforeUpload = (file: File) => {
  const isTxt = file.type === "text/plain";
  if (!isTxt) {
    importStatus.value = {
      message: "只能上传 TXT 文件！",
      type: "error",
    };
    return false;
  }
  return true;
};

// 文件变化处理
const handleChange = (info: UploadChangeParam) => {
  fileList.value = info.fileList.slice(-1); // 只保留最后上传的文件
};

// 清除文件
const clearFile = () => {
  fileList.value = [];
  importStatus.value = null;
  console.log("clearFileID");
};


const isInteger = (str) => {
  if (typeof str !== 'string') {
    return false; // 确保输入是字符串
  }
  const num = parseInt(str, 10); // 使用 parseInt 并指定进制
  return !isNaN(num) && num.toString() === str && Number.isInteger(num);
};

 
// 处理导入
const handleImport = async () => {
  console.log("handleImportID");
  if (fileList.value.length === 0) {
    importStatus.value = {
      message: "请先选择要导入的文件",
      type: "error",
    };
    return;
  }

  loading.value = true;
  importStatus.value = {
    message: "正在解析文件...",
    type: "info",
  };

  try {
    const file = fileList.value[0].originFileObj;
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        // 按行分割文本内容并过滤空行
        const lines = content.split('\n').filter(line => line.trim());
        
        // 将每行数据转换为所需格式，只有 strAccount
        const listTmp = lines.map(line => {
          const strTelegramNo = line.trim();
          const isNum = isInteger(strTelegramNo);
          if(isNum)
          {
              return {
              strTelegramNo,
              strAccount:"",
              strNickName: "",
              strPhone: "",
              strManage: ""
            };
          }
          else
          {
              return {
                strTelegramNo:"",
                strAccount:"",
                strNickName: "",
                strPhone: "",
                strManage: ""
              };
          }

        });
        let list = listTmp.filter((value) => value.strTelegramNo != "");
        console.log("解析后的数据:", list);
        console.log("数据条数:", list.length);

        const client = TelegramClient.getInstance();
        const result = await client.loadPrivateChatList(list);

        if (result && result.code === 1) {
          currentStep.value = 2;
          importStatus.value = {
            message: result.msg,
            type: "success",
          };
          emit("import-id-success", list);
        } else {
          importStatus.value = {
            message: `导入失败: ${result?.msg || "未知错误"}`,
            type: "error",
          };
          emit("import-id-error", result?.msg || "导入失败");
        }
      } catch (error) {
        const errorMessage = (error as Error).message;
        importStatus.value = {
          message: "文件解析失败：" + errorMessage,
          type: "error",
        };
        emit("import-id-error", errorMessage);
      }
    };

    reader.onerror = () => {
      const errorMessage = "文件读取失败";
      importStatus.value = {
        message: errorMessage,
        type: "error",
      };
      emit("import-id-error", errorMessage);
    };

    reader.readAsText(file); // 修改为文本方式读取
  } catch (error) {
    const errorMessage = "导入过程出错：" + (error as Error).message;
    importStatus.value = {
      message: errorMessage,
      type: "error",
    };
    emit("import-id-error", errorMessage);
  } finally {
    loading.value = false;
  }
};

// 添加删除所有私聊的处理函数
const handleDeleteAll = async () => {
  try {
    deleteLoading.value = true;
    const client = TelegramClient.getInstance();
    const account = localStorage.getItem("username") || "";
    const token = "1234567890";
    const result = await client.deleteAllPrivateChat(account, token);
    
    // 确保在删除操作后显示提示
    currentStep.value = 1; // 切换到结果显示状态
    
    if (result && result.code === 1) {
      importStatus.value = {
        message: result.msg,
        type: "success",
      };
    } else {
      importStatus.value = {
        message: `删除失败: ${result?.msg || "未知错误"}`,
        type: "error",
      };
    }
  } catch (error) {
    currentStep.value = 1; // 错误时也切换状态
    const errorMessage = "删除过程出错：" + (error as Error).message;
    importStatus.value = {
      message: errorMessage,
      type: "error",
    };
  } finally {
    deleteLoading.value = false;
  }
};
</script>

<style scoped>
.import-id-container {
  padding: 16px;
  background: #f8f9fa;
}

.import-id-wrapper {
  width: 100%;
  max-width: 580px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.page-header {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.step-dots {
  display: flex;
  gap: 6px;
}

.dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #e8e8e8;
  transition: all 0.3s ease;
}

.dot.active {
  background: #1890ff;
}

.main-content {
  padding: 24px;
}

.upload-zone, .result-zone {
  width: 100%;
}

.uploader {
  background: #fafafa;
  border: 1px dashed #e8e8e8;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.uploader:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}

.upload-inner {
  padding: 24px 16px;
  text-align: center;
}

.icon {
  font-size: 24px;
  color: #1890ff;
  margin-bottom: 8px;
}

.text-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.main-text {
  font-size: 14px;
  color: #262626;
  margin: 0;
}

.sub-text {
  font-size: 12px;
  color: #8c8c8c;
  margin: 0;
}

.upload-actions {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 16px;
}

.stat-box {
  background: #fafafa;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 18px;
  font-weight: 500;
  color: #262626;
  margin-bottom: 4px;
}

.stat-number.success {
  color: #52c41a;
}

.stat-label {
  font-size: 12px;
  color: #8c8c8c;
}

.footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-bar {
  margin-bottom: 16px;
}

:deep(.ant-upload-drag) {
  border: none;
  background: transparent;
}

/* 动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.alert-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 500px;
  z-index: 1000;
}

.custom-alert {
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.custom-alert.success) {
  background: #f6ffed;
  border-left: 4px solid #52c41a;
}

:deep(.custom-alert.error) {
  background: #fff2f0;
  border-left: 4px solid #ff4d4f;
}

:deep(.ant-alert-message) {
  color: #262626;
}

:deep(.ant-alert-icon) {
  font-size: 16px;
}

/* 添加动画效果 */
@keyframes slideDown {
  from {
    transform: translate(-50%, -100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

.alert-container {
  animation: slideDown 0.3s ease-out;
}
</style>
